import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
    Sparkles,
    Crown,
    Camera,
    Heart,
    GraduationCap,
    Star,
    ArrowUpRight,
    Check,
} from "lucide-react";
import { useCollectionData } from "../hooks/useCollectionData";
import { useSectionVisibility } from "../hooks/useSectionVisibility";
import { getAll } from "../services/firestoreService";
import { SERVICES as FALLBACK_SERVICES, PRICELIST as FALLBACK_PRICELIST } from "../data/content";

const ICONS = {
    Sparkles,
    Crown,
    Camera,
    Heart,
    GraduationCap,
    Star,
};

const getServiceImage = (s) => {
    if (s.image) return s.image;
    if (s.imageRef?.content) return s.imageRef.content;
    if (typeof s.imageRef === "string" && s.imageRef.startsWith("http")) return s.imageRef;
    if (typeof s.imageRef === "string") return s.imageRef;
    return "https://images.unsplash.com/photo-1547934659-7fa699ef3ce0?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
};

export default function Services() {
    const visibility = useSectionVisibility();
    const { data: services } = useCollectionData("services", FALLBACK_SERVICES);

    const [pricelist, setPricelist] = useState(FALLBACK_PRICELIST);

    useEffect(() => {
        const fetchPricelist = async () => {
            try {
                const items = await getAll("pricelist");
                if (items.length > 0) {
                    const reguler = items.find(
                        (i) => i.id === "reguler" || i.id?.includes("reguler")
                    );
                    const premium = items.find(
                        (i) => i.id === "premium" || i.id?.includes("premium")
                    );
                    setPricelist({
                        reguler: reguler?.data || reguler || FALLBACK_PRICELIST.reguler,
                        premium: premium?.data || premium || FALLBACK_PRICELIST.premium,
                    });
                }
            } catch (e) {
                console.warn("Pricelist unavailable, using fallback");
            }
        };
        fetchPricelist();
    }, []);

    if (!visibility.services) return null;

    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative bg-[#0a0908] py-28 sm:py-36 border-t border-[#d4af37]/10"
        >
            <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-16">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
                    <div>
                        <div className="eyebrow mb-6">Layanan & Tarif</div>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#fdfbf7] font-light leading-[1.05] tracking-tight max-w-2xl">
                            Setiap momen,
                            <span className="block italic text-[#d4af37]">
                                karya yang berbeda.
                            </span>
                        </h2>
                    </div>
                    <p className="text-[#a6a196] max-w-md text-base leading-relaxed">
                        Pilihan layanan individual dengan tarif terbuka,
                        bebas tanpa biaya tersembunyi. Sesi pernikahan
                        lengkap tersedia pada paket khusus di bawah.
                    </p>
                </div>

                {/* Service cards (visual) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#d4af37]/10 mb-24">
                    {services.map((s, i) => {
                        const Icon = ICONS[s.icon] || Sparkles;
                        return (
                            <motion.article
                                key={s.id || i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    delay: i * 0.08,
                                    duration: 0.7,
                                }}
                                data-testid={`service-${s.id}`}
                                className="group relative bg-[#0a0908] p-8 lg:p-10 min-h-[460px] flex flex-col justify-end overflow-hidden cursor-pointer"
                            >
                                <div className="absolute inset-0 z-0">
                                    <img
                                        src={getServiceImage(s)}
                                        alt={s.title}
                                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 opacity-50 group-hover:opacity-80"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908] via-[#0a0908]/85 to-[#0a0908]/30" />
                                </div>

                                <div className="absolute top-8 right-8 z-10 w-14 h-14 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] group-hover:bg-[#d4af37] group-hover:text-[#0a0908] transition-all duration-700">
                                    <Icon size={20} strokeWidth={1.2} />
                                </div>

                                <div className="absolute top-8 left-8 z-10 font-display text-xs tracking-[0.3em] text-[#d4af37]/70">
                                    0{i + 1}
                                </div>

                                <div className="relative z-10">
                                    <p className="text-[10px] tracking-[0.35em] uppercase text-[#d4af37] mb-3">
                                        {s.subtitle}
                                    </p>
                                    <h3 className="font-serif text-3xl lg:text-4xl text-[#fdfbf7] font-light mb-4 group-hover:translate-x-1 transition-transform duration-500">
                                        {s.title}
                                    </h3>
                                    <p className="text-sm text-[#a6a196] leading-relaxed mb-6">
                                        {s.description}
                                    </p>
                                    <div className="flex items-center justify-between border-t border-[#d4af37]/20 pt-4">
                                        <span className="text-[10px] tracking-[0.3em] uppercase text-[#a6a196]">
                                            Mulai{" "}
                                            <span className="text-[#d4af37] font-semibold">
                                                Rp {s.priceFrom}
                                            </span>
                                        </span>
                                        <ArrowUpRight
                                            size={18}
                                            className="text-[#d4af37] group-hover:rotate-45 transition-transform duration-500"
                                        />
                                    </div>
                                </div>
                            </motion.article>
                        );
                    })}
                </div>

                {/* PRICE LIST — Reguler & Premium */}
                <div className="text-center mb-12">
                    <div className="eyebrow mb-5 justify-center">
                        Daftar Tarif Lengkap
                    </div>
                    <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#fdfbf7] font-light leading-tight">
                        Price <span className="italic text-[#d4af37]">List</span>
                    </h3>
                </div>

                <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
                    {/* Reguler */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        data-testid="pricelist-reguler"
                        className="relative bg-[#15120f] border border-[#d4af37]/15 p-8 sm:p-10"
                    >
                        <div className="flex items-center gap-3 mb-2">
                            <span className="text-[10px] tracking-[0.4em] uppercase text-[#d4af37]">
                                {pricelist.reguler.label}
                            </span>
                            <span className="h-px flex-1 bg-[#d4af37]/30" />
                        </div>
                        <p className="font-serif text-2xl text-[#fdfbf7] italic mb-8">
                            {pricelist.reguler.subtitle}
                        </p>

                        <ul className="space-y-5">
                            {pricelist.reguler.items.map((it, i) => (
                                <li
                                    key={i}
                                    data-testid={`reguler-item-${i}`}
                                    className="flex items-end justify-between gap-4 pb-4 border-b border-dashed border-[#d4af37]/15 last:border-0"
                                >
                                    <div>
                                        <p className="font-serif text-lg sm:text-xl text-[#fdfbf7] mb-1">
                                            {it.name}
                                        </p>
                                        <p className="text-xs text-[#a6a196] leading-relaxed">
                                            {it.desc}
                                        </p>
                                    </div>
                                    <p className="font-display text-2xl sm:text-3xl text-[#d4af37] shrink-0">
                                        {it.price}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Premium */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        data-testid="pricelist-premium"
                        className="relative bg-gradient-to-b from-[#1a1816] to-[#0a0908] border border-[#d4af37] p-8 sm:p-10 shadow-[0_0_40px_-10px_rgba(212,175,55,0.25)]"
                    >
                        <div className="absolute -top-3 left-8 bg-[#d4af37] text-[#0a0908] px-4 py-1 text-[9px] tracking-[0.35em] uppercase font-bold">
                            Recommended
                        </div>
                        <div className="flex items-center gap-3 mb-2">
                            <span className="text-[10px] tracking-[0.4em] uppercase text-[#d4af37]">
                                {pricelist.premium.label}
                            </span>
                            <span className="h-px flex-1 bg-[#d4af37]/30" />
                        </div>
                        <p className="font-serif text-2xl text-[#fdfbf7] italic mb-8">
                            {pricelist.premium.subtitle}
                        </p>

                        <ul className="space-y-5 mb-8">
                            {pricelist.premium.items.map((it, i) => (
                                <li
                                    key={i}
                                    data-testid={`premium-item-${i}`}
                                    className="flex items-end justify-between gap-4 pb-4 border-b border-dashed border-[#d4af37]/20 last:border-0"
                                >
                                    <div>
                                        <p className="font-serif text-lg sm:text-xl text-[#fdfbf7] mb-1">
                                            {it.name}
                                        </p>
                                        <p className="text-xs text-[#a6a196] leading-relaxed">
                                            {it.desc}
                                        </p>
                                    </div>
                                    <p className="font-display text-2xl sm:text-3xl text-[#d4af37] shrink-0">
                                        {it.price}
                                    </p>
                                </li>
                            ))}
                        </ul>

                        {/* Freebies */}
                        <div className="bg-[#0a0908]/60 border border-[#d4af37]/15 p-5">
                            <p className="text-[10px] tracking-[0.35em] uppercase text-[#d4af37] mb-3">
                                Termasuk Gratis
                            </p>
                            <div className="flex flex-wrap gap-2 mb-3">
                                {pricelist.premium.freebies?.map((f, i) => (
                                    <span
                                        key={i}
                                        className="inline-flex items-center gap-1.5 bg-[#d4af37]/10 text-[#fdfbf7] text-xs px-3 py-1.5 border border-[#d4af37]/20"
                                    >
                                        <Check size={12} className="text-[#d4af37]" />
                                        {f}
                                    </span>
                                ))}
                            </div>
                            <p className="text-[10px] italic text-[#a6a196]">
                                NB: {pricelist.premium.note}
                            </p>
                        </div>
                    </motion.div>
                </div>

                <p className="mt-10 text-center text-[10px] tracking-[0.3em] uppercase text-[#a6a196]">
                    Harga belum termasuk transport untuk lokasi luar
                    Jabodetabek · Hubungi kami untuk paket kustom
                </p>
            </div>
        </section>
    );
}
